<template lang="pug">
#waiter-progress-history-register(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 経過記録の登録

    .box-body.register-body
      FormDatetimePicker(v-model="datetime" :date.sync="datetimeDate" :time.sync="datetimeTime"
        key-name="日時" name="datetime" placeholder="日時" :validation-rules="{ required: true }" required)
      FormInput(
        v-model="title"
        key-name="タイトル" name="title" type="text" placeholder="タイトル"
        :validation-rules="{ required: true, max: 100 }" :maxlength="100"
        :styles="{ maxWidth: '720px', width: '720px' }" required)
      FormTextarea(
        v-model="text"
        key-name="本文" name="text" placeholder="本文"
        :validation-rules="{ required: true, max: 1000 }" :maxlength="1000"
        :styles="{ maxWidth: '720px', width: '720px', minHeight: '160px' }" required)

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.register-btn(@click="showConfirmModal" type="button") 登録
        button.btn.cancel-btn(@click="gotoWaiterDetail" type="button") キャンセル

  OkCancelModal(v-model="showRegisterModal" title="経過記録を登録しますか？" @ok="registerHistory")
  OkModal(v-model="showRegisterSuccessModal" title="登録に成功しました。" @ok="gotoWaiterDetail")
  
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";

import { userStore } from "@/store/store";
import { PcUrl } from "@/commons/constants/PcUrl";

import WaiterProgressHistoriesService, { WaiterProgressHistoriesPostModel } from "@/commons/services/WaiterProgressHistories";

import PageLoading from "@/pc/basics/PageLoading.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormDatetimePicker from "@/pc/components/forms/FormDatetimePicker.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";

/**
 * ### 待機者登録画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    ValidationAlert,
    FormDatetimePicker,
    FormInput,
    FormTextarea
  },
  $_veeValidate: { validator: "new" }
})
export default class WaiterProgressHistoryRegister extends Vue {
  /** 待機者経過履歴サービス */
  @Inject("WaiterProgressHistoriesService") waiterProgressHistoriesService!: WaiterProgressHistoriesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showRegisterModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 開始日時 */
  datetime: number | null = null;
  /** 開始日時(日) */
  datetimeDate: string = "";
  /** 開始日時(時) */
  datetimeTime: string = "";
  /** タイトル */
  title: string = "";
  /** 本文 */
  text: string = "";

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle mounted
   */
  public mounted(): void {
    const datetime: number = this.$dateUtil.convertDateToNumber(new Date(), "yyyymmddhhMMss000");
    this.datetime = datetime;
    this.datetimeDate = this.$dateUtil.convertNumberToString(datetime, "yyyy-mm-dd");
    this.datetimeTime = this.$dateUtil.convertNumberToString(datetime, "hh:MM:00");

    return;
  }

  /**
   * ### 登録確認モーダルを表示する
   *
   * - 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   */
  public async showConfirmModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();

    if (validationResult) {
      this.showValidationAlert = false;
      this.showRegisterModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### 経過記録を登録する
   */
  public async registerHistory(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    const residentIdInSystem: number = Number(this.$route.params.idinsystem);
    const requestBody: WaiterProgressHistoriesPostModel = await this.createRequestBody();
    await this.waiterProgressHistoriesService.post(userStore, residentIdInSystem, requestBody);

    this.isBusy = false;
    this.showRegisterSuccessModal = true;

    return;
  }

  /**
   * ### リクエストボディを作成する
   */
  public async createRequestBody(): Promise<WaiterProgressHistoriesPostModel> {
    const requestBody: WaiterProgressHistoriesPostModel = {
      facilityId: userStore.loginUser.facilityId,
      datetime: this.datetime as number,
      title: this.title,
      text: this.text
    };

    return requestBody;
  }

  /**
   * ### 待機者詳細に遷移する
   */
  public gotoWaiterDetail(): void {
    this.$router.push({
      name: PcUrl.WaiterDetail,
      params: { facilityid: this.$route.params.facilityid, idinsystem: this.$route.params.idinsystem, residentid: this.$route.params.residentid }
    });
    return;
  }
}
</script>

<style lang="scss" scoped>
#waiter-progress-history-register {
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  .register-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
